/**
 * SIL-1347 - the 220px marketplace filter card. Handoff section 1: checkbox
 * groups for GPU model, Listing type, Condition and Region, plus a price range
 * and a Clear link. Values within a group OR; groups AND - which is the server's
 * semantics, not this component's: every change goes straight into the URL and
 * back out through `GET /api/client/marketplace`.
 *
 * Facet OPTIONS come from the listings the API returned, unioned with whatever is
 * currently selected. The union is the load-bearing half: a selected value whose
 * own filter removed every other listing must stay tickable, or a buyer can check
 * a box and then be unable to uncheck it.
 *
 * SIL-1377: every per-listing read goes through `listingShape`, because this
 * function runs over EVERY row before anything renders - it was crash site one of
 * two (`Cannot read properties of undefined (reading 'gpuModel')`), and a throw
 * here white-screens the whole page rather than spoiling one card.
 */
import { Card } from '@siliconx/shared/ui/Card';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import type { MarketplaceListing } from '@siliconx/shared/dto/marketplace';
import type { ListingType } from '@siliconx/shared/dto/listing';
import {
  LISTING_TYPE_LABELS,
  MARKETPLACE_FILTERS_CLEAR,
  MARKETPLACE_FILTERS_TITLE,
  MARKETPLACE_FILTER_ASSET_TYPE,
  MARKETPLACE_FILTER_CONDITION,
  MARKETPLACE_FILTER_MODEL,
  MARKETPLACE_FILTER_NO_OPTIONS,
  MARKETPLACE_FILTER_PRICE,
  MARKETPLACE_FILTER_PRICE_MAX,
  MARKETPLACE_FILTER_PRICE_MIN,
  MARKETPLACE_FILTER_REGION,
  MARKETPLACE_FILTER_TYPE,
} from '@siliconx/shared/ui/buyer-listing/copy/marketplace';
import {
  hasActiveMarketplaceSearch,
  toggleFacet,
  type MarketplaceFacetKey,
  type MarketplaceSearch,
} from './marketplaceSearch';
import { evidenceOf, technicalDetailsOf } from '@siliconx/shared/ui/buyer-listing/listingShape';
import { assetTypeIdsOf, assetTypeLabel, isFilterableAssetType } from './marketplaceListingHelpers';

/** SIL-1444 (spec decision 3): GPU MODEL has options only when a SKU's
 *  gpu_model attribute is present, which in practice only happens on GPU
 *  asset-type listings — but rather than assume that correlation, this
 *  gates explicitly: the group renders ONLY when 'gpu' is one of the
 *  ticked asset-type filter values, so a buyer who has not selected GPU
 *  never sees a GPU-specific facet for a browse that may be all servers and
 *  switches. */
const GPU_ASSET_TYPE_ID = 'gpu';

interface FacetOption {
  value: string;
  label: string;
}

/** Sorted, de-duplicated options for one group, including selected-but-absent values. */
function options(values: (string | null | undefined)[], selected: string[] | undefined): string[] {
  const set = new Set<string>();
  for (const v of values) if (typeof v === 'string' && v.length > 0) set.add(v);
  for (const v of selected ?? []) set.add(v);
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function marketplaceFacetOptions(
  listings: MarketplaceListing[],
  search: MarketplaceSearch,
): Record<MarketplaceFacetKey, FacetOption[]> {
  return {
    // SIL-1377 - a listing with no `technicalDetails` block contributes no model
    // option, exactly as one whose SKU carries no `gpuModel` attribute already
    // did. `options` drops every non-string, so the empty-set path below is
    // already correct; the bug was only the unguarded dereference.
    model: options(
      listings.map((l) => technicalDetailsOf(l).gpuModel),
      search.model,
    ).map((value) => ({ value, label: value })),
    type: options(
      listings.map((l) => l.listingType),
      search.type,
    ).map((value) => ({ value, label: LISTING_TYPE_LABELS[value as ListingType] ?? value })),
    condition: options(
      listings.map((l) => l.conditionBand),
      search.condition,
    ).map((value) => ({ value, label: conditionLabel(value) })),
    // SIL-1377 - region is the only facet sourced from the evidence block, so it
    // is the one that empties out under skew. An unticked group renders
    // MARKETPLACE_FILTER_NO_OPTIONS; a ticked value survives through `selected`,
    // so a buyer can still clear a region filter they cannot re-derive.
    region: options(
      listings.map((l) => evidenceOf(l)?.region ?? null),
      search.region,
    ).map((value) => ({ value, label: value })),
    // SIL-1444 (spec Change 3) - one option per asset type PRESENT in the
    // current listings, Cloud VM excluded even if present (the marketplace
    // does not offer it as a browsable asset type) and even if it somehow
    // arrives already selected via a hand-edited URL - `options()`'s
    // selected-value union is filtered through the same predicate, not
    // just the listing-derived values.
    asset_type: options(
      listings.flatMap((l) => assetTypeIdsOf(l)),
      search.asset_type,
    )
      .filter(isFilterableAssetType)
      .map((value) => ({ value, label: assetTypeLabel(value) })),
  };
}

function CheckboxGroup({
  title,
  groupKey,
  items,
  selected,
  onToggle,
  testId,
}: {
  title: string;
  groupKey: MarketplaceFacetKey;
  items: FacetOption[];
  selected: string[] | undefined;
  onToggle: (key: MarketplaceFacetKey, value: string) => void;
  /**
   * SIL-1444: the spec names `data-testid="filter-group-asset-type"` (hyphen)
   * for the new group, but `groupKey` is `'asset_type'` (underscore) to match
   * the URL/API param it round-trips through — every other group's testid
   * already coincides with its key, so only this one needs an override
   * rather than a second, drifting key spelling.
   */
  testId?: string;
}) {
  return (
    <fieldset data-testid={testId ?? `filter-group-${groupKey}`}>
      <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </legend>
      <div className="mt-2 flex flex-col gap-1.5">
        {items.length === 0 ? (
          <span className="text-xs text-muted-foreground">{MARKETPLACE_FILTER_NO_OPTIONS}</span>
        ) : (
          items.map((item) => (
            <label key={item.value} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="rounded border-border text-brand-600 focus:ring-brand-500"
                checked={(selected ?? []).includes(item.value)}
                onChange={() => onToggle(groupKey, item.value)}
              />
              <span className="truncate">{item.label}</span>
            </label>
          ))
        )}
      </div>
    </fieldset>
  );
}

export function MarketplaceFilters({
  search,
  listings,
  onChange,
  onClear,
}: {
  search: MarketplaceSearch;
  listings: MarketplaceListing[];
  onChange: (next: MarketplaceSearch) => void;
  onClear: () => void;
}) {
  const facets = marketplaceFacetOptions(listings, search);
  const toggle = (key: MarketplaceFacetKey, value: string) => onChange(toggleFacet(search, key, value));

  const setPrice = (key: 'price_min' | 'price_max', raw: string) => {
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? undefined : Number(trimmed);
    onChange({
      ...search,
      [key]: parsed !== undefined && Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined,
    });
  };

  return (
    <Card as="section" padding="md" aria-label={MARKETPLACE_FILTERS_TITLE} className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{MARKETPLACE_FILTERS_TITLE}</h2>
        {hasActiveMarketplaceSearch(search) && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-brand-600 hover:underline"
          >
            {MARKETPLACE_FILTERS_CLEAR}
          </button>
        )}
      </div>

      <CheckboxGroup
        title={MARKETPLACE_FILTER_ASSET_TYPE}
        groupKey="asset_type"
        testId="filter-group-asset-type"
        items={facets.asset_type}
        selected={search.asset_type}
        onToggle={toggle}
      />
      {/* SIL-1444 decision: GPU MODEL only when 'gpu' is a ticked asset
          type - otherwise this group either duplicates the asset-type
          group's job or renders "No options" for a browse with no GPU
          listings selected, per the spec's own call-out that the group
          empties out today. */}
      {(search.asset_type ?? []).includes(GPU_ASSET_TYPE_ID) && (
        <CheckboxGroup
          title={MARKETPLACE_FILTER_MODEL}
          groupKey="model"
          items={facets.model}
          selected={search.model}
          onToggle={toggle}
        />
      )}
      <CheckboxGroup
        title={MARKETPLACE_FILTER_TYPE}
        groupKey="type"
        items={facets.type}
        selected={search.type}
        onToggle={toggle}
      />
      <CheckboxGroup
        title={MARKETPLACE_FILTER_CONDITION}
        groupKey="condition"
        items={facets.condition}
        selected={search.condition}
        onToggle={toggle}
      />
      <CheckboxGroup
        title={MARKETPLACE_FILTER_REGION}
        groupKey="region"
        items={facets.region}
        selected={search.region}
        onToggle={toggle}
      />

      <fieldset data-testid="filter-group-price">
        <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {MARKETPLACE_FILTER_PRICE}
        </legend>
        <div className="mt-2 flex items-center gap-2">
          {/* Uncontrolled + commit on blur, so typing "12500" is one navigation
              rather than five. `key` ties the mounted input to the URL value, so
              Clear (which only changes the search) actually empties the box. */}
          <input
            key={`price-min-${search.price_min ?? ''}`}
            type="number"
            min={0}
            aria-label={MARKETPLACE_FILTER_PRICE_MIN}
            placeholder={MARKETPLACE_FILTER_PRICE_MIN}
            defaultValue={search.price_min ?? ''}
            onBlur={(e) => setPrice('price_min', e.target.value)}
            className="w-full min-w-0 rounded border border-border bg-card px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            key={`price-max-${search.price_max ?? ''}`}
            type="number"
            min={0}
            aria-label={MARKETPLACE_FILTER_PRICE_MAX}
            placeholder={MARKETPLACE_FILTER_PRICE_MAX}
            defaultValue={search.price_max ?? ''}
            onBlur={(e) => setPrice('price_max', e.target.value)}
            className="w-full min-w-0 rounded border border-border bg-card px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </fieldset>
    </Card>
  );
}
